import React from 'react';
import { useInlineLayout } from '../hooks/useInlineLayout';
import { useAuthViewport } from '@/epics/authentication/pages/login/AuthTemplate/useAuthViewport';

export interface InlineLayoutNavItem {
  id: string;
  label: string;
  /** Optional data-testid for the nav button (e.g. for E2E). */
  dataTestId?: string;
}

export interface InlineLayoutProps {
  /** Page title shown at the top */
  title: string;
  /** Optional content rendered to the right of the title (e.g. status toggle). */
  titleAction?: React.ReactNode;
  /** List of side nav items (id + label). Clicking one shows the matching content. */
  navItems: InlineLayoutNavItem[];
  /** Content to show per nav item id. Keys must match navItems[].id. */
  contentByNavId: Record<string, React.ReactNode>;
  /** Optional slot above the nav (e.g. profile card, filters). */
  header?: React.ReactNode;
  /** URL search param key for active tab (e.g. "tab"). When set, active nav syncs with URL. */
  searchParamKey?: string;
  /** Default active nav id when no valid param. Defaults to first nav item. */
  defaultActiveId?: string;
  /** Optional banner rendered between the header and the nav/content area (e.g. payment warning). */
  paymentBanner?: React.ReactNode;
  /** Override the side-nav width on desktop. Defaults to 200px. */
  navWidth?: string;
  /**
   * Tablet-only tab sizing. 'fill' (default) splits the row equally (flex-1);
   * 'fit' sizes each tab to its label and lets the row scroll horizontally.
   * No effect on desktop.
   */
  tabletNavVariant?: 'fill' | 'fit';
}

const InlineLayout: React.FC<InlineLayoutProps> = ({
  title,
  titleAction,
  navItems,
  contentByNavId,
  header,
  searchParamKey,
  defaultActiveId,
  paymentBanner,
  tabletNavVariant = 'fill',
}) => {
  const { activeId, handleNavClick } = useInlineLayout({
    navItems,
    ...(searchParamKey !== undefined && { searchParamKey }),
    ...(defaultActiveId !== undefined && { defaultActiveId }),
  });

  const activeContent = contentByNavId[activeId] ?? null;
  const { isTablet } = useAuthViewport();

  return (
    <div className="w-full h-full flex flex-col bg-white p-4 shadow-[0px_2px_4px_0px_#00000014] rounded-[4px]">
      <div className="flex items-center justify-between mb-6 py-[8px]">
        <h1 className="text-[22px] text-neutral-10">{title}</h1>
        {titleAction != null && <div className="shrink-0">{titleAction}</div>}
      </div>

      {header != null && (
        <>
          <div className="bg-white rounded-lg mb-5 flex items-start gap-6">
            {header}
          </div>
          {!isTablet && <div className="border-b border-[#DCDDE0]" />}
        </>
      )}

      {paymentBanner != null && <div className="mt-4">{paymentBanner}</div>}

      <div
        className={`flex ${isTablet ? 'flex-col' : 'flex-row'} gap-6 pt-5 flex-1 overflow-auto custom-scrollbar min-h-0`}
      >
        <nav
          className={`flex ${isTablet ? 'flex-row overflow-x-auto w-full border-b-2 border-[#DCDDE0]  gap-4' : 'flex-col w-[200px]'} gap-[2px] shrink-0`}
        >
          {navItems.map((item) => (
            <div
              key={item.id}
              className={
                isTablet
                  ? tabletNavVariant === 'fit'
                    ? 'shrink-0'
                    : 'flex-1'
                  : ''
              }
            >
              <button
                type="button"
                onClick={() => handleNavClick(item.id)}
                data-testid={item.dataTestId ?? item.id}
                className={`cursor-pointer p-[16px] w-full font-medium ${isTablet ? 'text-center' : 'text-start'} text-labelLarge transition-colors rounded-lg ${isTablet && tabletNavVariant === 'fit' ? 'whitespace-nowrap' : 'text-wrap'} ${
                  activeId === item.id
                    ? `text-primary-color-30 ${isTablet ? '' : 'bg-primary-color-90'}`
                    : 'text-on-surface-variant hover:text-neutral-20 hover:bg-neutral-95'
                }`}
              >
                {item.label}
              </button>
              {activeId === item.id && isTablet && (
                <div className="border-b-2 border-[#2A4455] rounded-t-[100px]" />
              )}
            </div>
          ))}
        </nav>

        {!isTablet && (
          <div
            className={`${isTablet ? 'border-b-2' : 'border-l'} border-[#DCDDE0] shrink-0`}
          />
        )}
        <div className="flex-1 overflow-auto custom-scrollbar min-w-0 flex flex-col">
          {activeContent}
        </div>
      </div>
    </div>
  );
};

export default InlineLayout;
